import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight, Check, CheckCheck, MessageCircle, Paperclip, Send, User, X, SlidersHorizontal } from './icons';
import './ChatUI.css';

export function useChatViewport() {
  const [viewport, setViewport] = useState({});
  useEffect(() => {
    const update = () => setViewport({ height: window.visualViewport?.height || window.innerHeight, top: window.visualViewport?.offsetTop || 0 });
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, []);
  return viewport;
}

function attachmentUrl(url) {
  if (!url) return '';
  try {
    const base = new URL(process.env.REACT_APP_API_URL || '/', window.location.origin);
    const resolved = new URL(url, base);
    return ['http:', 'https:'].includes(resolved.protocol) ? resolved.href : '';
  } catch { return ''; }
}

export function ChatTranscript({ messages, isTyping, viewerIsAdmin = false, followRef, onSeen, onRequestSupport, supportRequested = false }) {
  const scrollRef = useRef(null);
  const nearBottom = useRef(true);
  const previous = useRef(null);
  const [hasNew, setHasNew] = useState(false);
  const seenCallback = useRef(onSeen);
  seenCallback.current = onSeen;
  useEffect(() => {
    const root = scrollRef.current;
    const scan = () => {
      if (document.visibilityState !== 'visible' || !document.hasFocus() || !seenCallback.current) return;
      const bounds = root.getBoundingClientRect();
      const ids = [...root.querySelectorAll('[data-unread="true"]')].filter(element => {
        const box = element.getBoundingClientRect();
        return box.height > 0 && box.top < bounds.bottom && box.bottom > bounds.top;
      }).map(element => Number(element.dataset.messageId));
      for (let index = 0; index < ids.length; index += 100) seenCallback.current(ids.slice(index, index + 100));
    };
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(scan, { root });
    root.querySelectorAll('[data-unread="true"]').forEach(element => observer?.observe(element));
    root.addEventListener('scroll', scan);
    window.addEventListener('focus', scan);
    document.addEventListener('visibilitychange', scan);
    scan();
    return () => { observer?.disconnect(); root.removeEventListener('scroll', scan); window.removeEventListener('focus', scan); document.removeEventListener('visibilitychange', scan); };
  }, [messages, viewerIsAdmin]);
  const bottom = useCallback(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
    nearBottom.current = true;
    setHasNew(false);
  }, []);
  useEffect(() => {
    const latest = messages[messages.length - 1];
    const key = latest ? `${latest.id}-${latest.created_at}` : null;
    const changed = key !== previous.current;
    if (nearBottom.current || followRef?.current) {
      bottom();
      if (followRef) followRef.current = false;
    } else if (changed) setHasNew(true);
    previous.current = key;
  }, [messages, isTyping, followRef, bottom]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { if (nearBottom.current) bottom(); });
    if (scrollRef.current) observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [bottom]);
  return (
    <div className="chat-transcript-wrap">
      <div className="chat-transcript" ref={scrollRef} role="log" aria-label="پیام‌های گفتگو" aria-live="polite" onScroll={() => {
        const element = scrollRef.current;
        nearBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 64;
        if (nearBottom.current) setHasNew(false);
      }}>
        <div className="chat-message-list">
          {messages.length === 0 && <div className="chat-empty">گفتگو را با ارسال یک پیام شروع کنید.</div>}
          {messages.map(msg => {
            const admin = Number(msg.is_admin) === 1;
            const system = Number(msg.is_system) === 1;
            const own = !system && admin === viewerIsAdmin;
            const url = attachmentUrl(msg.file_url);
            return (
              <div className={`chat-message-row ${system ? 'chat-system' : own ? 'chat-own' : 'chat-other'}`} data-message-id={msg.id} data-unread={admin !== viewerIsAdmin && Number(msg.is_read) !== 1} key={`${msg.id}-${msg.created_at}`}>
                <div className="chat-bubble">
                  {system && <strong className="chat-system-label">پیام خودکار سیستم</strong>}
                  {msg.message_text && <p dir="auto" className="chat-message-text">{msg.message_text}</p>}
                  {system && <a className="chat-support-phone" href="tel:09215316012">تماس با پشتیبانی: <bdi>۰۹۲۱۵۳۱۶۰۱۲</bdi></a>}
                  {system && msg.system_kind?.startsWith('faq_') && !viewerIsAdmin && onRequestSupport && <button type="button" className="chat-request-support" disabled={supportRequested} onClick={onRequestSupport}>{supportRequested ? 'درخواست پاسخ پشتیبانی ارسال شد' : 'درخواست پاسخ پشتیبانی'}</button>}
                  {url && (msg.file_type === 'image' ?
                    <a href={url} target="_blank" rel="noopener noreferrer" className="chat-attachment"><img src={url} alt="تصویر پیوست" onLoad={() => { if (nearBottom.current) bottom(); }} /></a> :
                    <audio controls src={url} className="chat-audio" aria-label="پیام صوتی" />)}
                  <div className="chat-message-meta">
                    <time dateTime={msg.created_at}>{new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}</time>
                    {own && (Number(msg.legacy_read_unknown) === 1 ? <Check size={14} aria-label="وضعیت خواندن پیام قدیمی مشخص نیست" /> : Number(msg.is_read) === 1 ? <CheckCheck className="chat-read-check" size={14} aria-label="خوانده شده" /> : <Check size={14} aria-label="هنوز خوانده نشده" />)}
                  </div>
                </div>
              </div>
            );
          })}
          {isTyping && <div className="chat-message-row chat-other"><div className="chat-typing" role="status">در حال نوشتن<span aria-hidden="true">…</span></div></div>}
        </div>
      </div>
      {hasNew && <button className="chat-new-messages" onClick={bottom}><ArrowDown size={16} /> پیام‌های جدید</button>}
    </div>
  );
}

export function ChatComposer({ message, setMessage, file, setFile, onSend, busy = false, ready = true }) {
  const fileRef = useRef(null);
  const textRef = useRef(null);
  useEffect(() => {
    const element = textRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 120)}px`;
  }, [message]);
  const removeFile = () => { setFile(null); if (fileRef.current) fileRef.current.value = ''; };
  useEffect(() => { if (!file && fileRef.current) fileRef.current.value = ''; }, [file]);
  const canSend = ready && !busy && (message.trim() || file);
  return (
    <form className="chat-composer" onSubmit={event => { event.preventDefault(); if (canSend) onSend(); }}>
      {file && <div className="chat-file-preview"><Paperclip size={16} /><span title={file.name}>{file.name}</span><button type="button" aria-label="حذف پیوست" onClick={removeFile} disabled={busy}><X size={18} /></button></div>}
      <div className="chat-composer-row">
        <textarea ref={textRef} rows={1} value={message} onChange={event => setMessage(event.target.value)} aria-label="متن پیام" placeholder="پیام خود را بنویسید…" disabled={busy} dir={message.trim() ? 'auto' : 'rtl'} onKeyDown={event => {
          const touch = window.matchMedia?.('(pointer: coarse)').matches;
          if (event.key === 'Enter' && !event.shiftKey && !touch && !event.nativeEvent.isComposing) {
            event.preventDefault();
            if (canSend) onSend();
          }
        }} />
        <input ref={fileRef} type="file" accept="image/*,audio/*" onChange={event => setFile(event.target.files?.[0] || null)} hidden />
        <button type="button" className="chat-icon-button chat-attach-button" aria-label="افزودن تصویر یا صدا" onClick={() => fileRef.current.click()} disabled={busy}><Paperclip size={20} /></button>
        <button type="submit" className="chat-icon-button chat-send-button" aria-label={busy ? 'در حال ارسال' : 'ارسال پیام'} disabled={!canSend}><Send size={20} /></button>
      </div>
    </form>
  );
}

export function ChatAdminView({ rooms, selectedRoom, selectRoom, goBack, navigate, messages, isTyping, followRef, message, setMessage, file, setFile, sendMessage, busy, connected, onSeen, search, setSearch, searchUsers, searchResults, searching, startUser, searchDone, onManageReplies }) {
  const viewport = useChatViewport();
  return (
    <div className="chat-viewport" dir="rtl" style={viewport}>
      <div className="chat-shell chat-admin-shell" data-room-open={Boolean(selectedRoom)}>
        <aside className="chat-sidebar" id="sidebar" aria-label="فهرست گفتگوها">
          <header className="chat-header"><MessageCircle size={22} className="text-indigo-600" /><h1 className="flex-1">گفتگوهای پشتیبانی</h1>{onManageReplies && <button className="chat-icon-button" aria-label="تنظیمات پاسخ‌های خودکار" title="پاسخ‌های خودکار" onClick={onManageReplies}><SlidersHorizontal size={20} /></button>}<button className="chat-icon-button" aria-label="بازگشت به داشبورد" onClick={() => navigate('/')}><ArrowRight size={20} /></button></header>
          <form className="chat-search" onSubmit={event => { event.preventDefault(); searchUsers(); }}>
            <label htmlFor="chat-user-search">شروع گفتگو با کاربر</label>
            <div><input id="chat-user-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="نام یا شماره تماس" /><button type="submit" disabled={searching || !search?.trim()}>{searching ? '…' : 'جست‌وجو'}</button></div>
          </form>
          <div className="chat-room-list">
            {searchDone && <div className="chat-search-results"><p>نتیجه جست‌وجو</p>{searchResults.length === 0 && <p>کاربری یافت نشد.</p>}{searchResults.map(user => <button className="chat-room" key={user.id} onClick={() => startUser(user)} disabled={searching}><strong>{[user.first_name,user.last_name].filter(Boolean).join(' ') || 'کاربر'}</strong><span dir="ltr">{user.phone_number}</span><small>شروع / ادامه گفتگو</small></button>)}</div>}
            {rooms.length === 0 && <p className="chat-empty">گفتگویی وجود ندارد.</p>}
            {rooms.map(room => <button key={room.id} onClick={() => selectRoom(room)} className={`chat-room ${selectedRoom?.id === room.id ? 'chat-room-selected' : ''}`} aria-pressed={selectedRoom?.id === room.id}>
              <div className="flex items-center gap-2"><span className="font-medium truncate flex-1 min-w-0">{room.user_name}</span>{room.unread_count > 0 && <span className="bg-indigo-600 text-white rounded-full px-2 py-0.5 text-xs shrink-0" aria-label="پیام‌های خوانده نشده">{room.unread_count}</span>}</div>
              <p className="chat-room-preview" dir="auto">{room.last_message || 'بدون پیام'}</p>
              {room.last_message_time && <time className="text-xs text-slate-400 block mt-2">{new Date(room.last_message_time).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}</time>}
            </button>)}
          </div>
        </aside>
        <section className="chat-admin-content" id="chat" aria-label="گفتگوی انتخاب‌شده">
          {selectedRoom ? <>
            <header className="chat-header"><button className="chat-icon-button chat-mobile-back" aria-label="بازگشت به فهرست گفتگوها" onClick={goBack}><ArrowRight size={20} /></button><div className="chat-header-avatar"><User size={22} /></div><div className="min-w-0"><h2 className="truncate">{selectedRoom.user_name}</h2><p dir="ltr">{selectedRoom.user_mobile}</p></div></header>
            <ChatTranscript key={selectedRoom.id} messages={messages} isTyping={isTyping} viewerIsAdmin followRef={followRef} onSeen={onSeen} />
            {!connected && <p className="chat-notice" role="status">در حال اتصال به گفتگو…</p>}
            <ChatComposer message={message} setMessage={setMessage} file={file} setFile={setFile} onSend={sendMessage} busy={busy} ready={connected} />
          </> : <div className="chat-empty">برای مشاهده پیام‌ها یک گفتگو را انتخاب کنید.</div>}
        </section>
      </div>
    </div>
  );
}
